import type { TravelSearchSelection } from '../../../api/travel-search-config'

import { useEffect } from 'react'

import { LoadDataFromParams } from '../../Utils/url-helpers'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { getBaseApi, getLocaleFromUrlOrQuery } from '../../Utils/common'
import { fetcher } from '../../Utils/fetcher'

import { RoutesResponse } from './route-types'

export const useLoadRoutesIntoContext = (selection?: TravelSearchSelection) => {
  const { dispatch } = useTravelSearchContext()
  const {
    queryData: { offerCode, routeCode },
  } = LoadDataFromParams()
  const source = selection?.source
  const minibookId = selection?.source === 'legacy' ? selection.minibookId : undefined
  const travelSearchId = selection?.source === 'shared' ? selection.travelSearchId : undefined

  useEffect(() => {
    if (!source || (!minibookId && !travelSearchId)) return
    const controller = new AbortController()

    const fetchRoutes = async () => {
      dispatch({ payload: true, type: 'SET_ROUTES_API_LOADING' })

      const baseApi = getBaseApi()
      const locale = getLocaleFromUrlOrQuery()
      const params = new URLSearchParams({ contentSource: source, locale })
      if (minibookId) params.set('minibookId', minibookId)
      if (travelSearchId) params.set('travelSearchId', travelSearchId)
      if (offerCode) params.set('offerCode', offerCode)
      const url = `${baseApi}/routes?${params}`

      try {
        const routesApiData = await fetcher<RoutesResponse>(url, { signal: controller.signal })
        if (controller.signal.aborted) return

        let route = routesApiData.defaultRoute ?? routesApiData.categories?.[0]?.routes?.[0]

        if (routeCode) {
          const found = routesApiData.categories.flatMap((c) => c.routes).find((r) => r.routeCode === routeCode)

          if (found) route = found
        }

        dispatch({ route, type: 'SET_ROUTE' })
        if (routesApiData.oneWay) {
          dispatch({ isOneWay: true, type: 'SET_ONEWAY' })
        }
        dispatch({ payload: routesApiData, type: 'SET_ROUTES_API_DATA' })
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to fetch travel search routes:', error)
        }
      } finally {
        if (!controller.signal.aborted) {
          dispatch({ payload: false, type: 'SET_ROUTES_API_LOADING' })
        }
      }
    }

    fetchRoutes()
    return () => {
      controller.abort()
      dispatch({ payload: false, type: 'SET_ROUTES_API_LOADING' })
    }
  }, [source, minibookId, travelSearchId, offerCode, routeCode, dispatch])
}
